import { logError } from '../lib/logger';
import {
  MODAL_TAP_GUARD_MS,
  isWithinModalTapGuard,
  setMediaModalOpen,
  shouldUseBackdropDismiss,
} from '../lib/mediaModal';
import { markProgressCompleteOnDownload, tryAutoCompleteDocumentOnClose } from '../lib/progressAutoComplete';
import React, { useCallback, useEffect, useMemo, useRef, useState, Suspense } from 'react';
import axios from 'axios';
import { X, Download, Loader2, ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import PinchZoom from './PinchZoom';

const PdfViewer = React.lazy(() => import('./PdfViewer'));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BACKDROP_CLOSE_GUARD_MS = 1200;

const DocumentModal = ({ content, onClose, engagementSource = 'main' }) => {
  const [downloading, setDownloading] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [imagePreviewLoading, setImagePreviewLoading] = useState(false);
  const imagePreviewBlobRef = useRef(null);
  const openedAtRef = useRef(null);
  const openedGuardUntilRef = useRef(0);
  const tapGuardUntilRef = useRef(0);
  const allowBackdropDismiss = shouldUseBackdropDismiss();

  const handleClose = useCallback(() => {
    if (isWithinModalTapGuard(tapGuardUntilRef.current)) return;
    tryAutoCompleteDocumentOnClose(content?.id, openedAtRef.current);
    onClose();
  }, [content?.id, onClose]);

  const handleBackdropClose = useCallback(() => {
    if (!allowBackdropDismiss) return;
    if (Date.now() < openedGuardUntilRef.current) return;
    handleClose();
  }, [allowBackdropDismiss, handleClose]);

  useEffect(() => {
    if (!content?.id) {
      setMediaModalOpen(false);
      return undefined;
    }
    const now = Date.now();
    openedAtRef.current = now;
    setMediaModalOpen(true);
    tapGuardUntilRef.current = now + MODAL_TAP_GUARD_MS;
    openedGuardUntilRef.current = now + BACKDROP_CLOSE_GUARD_MS;

    const swallowGhostTap = (e) => {
      if (!isWithinModalTapGuard(tapGuardUntilRef.current)) return;
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('click', swallowGhostTap, true);
    document.addEventListener('touchend', swallowGhostTap, true);

    return () => {
      document.removeEventListener('click', swallowGhostTap, true);
      document.removeEventListener('touchend', swallowGhostTap, true);
      setMediaModalOpen(false);
    };
  }, [content?.id]);

  useEffect(() => {
    if (content?.id) {
      axios.post(`${API}/progress/view`, { content_id: content.id, source: engagementSource })
        .catch(e => logError('DocumentModal:view', e));
    }
  }, [content?.id, engagementSource]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [handleClose]);

  const fileUrl = useMemo(() => {
    if (!content?.file_path) return null;
    return `${API}/files/${content.file_path}`;
  }, [content?.file_path]);

  const fileName = content.file_name || 'documento';
  const isPdf = fileName.toLowerCase().endsWith('.pdf');
  const isImage = /\.(png|jpe?g|gif|webp|bmp)$/i.test(fileName);

  useEffect(() => {
    if (!isImage || !fileUrl || !content?.file_path) {
      setImagePreviewUrl(null);
      setImagePreviewLoading(false);
      imagePreviewBlobRef.current = null;
      return;
    }
    let cancelled = false;
    let objectUrl = null;
    imagePreviewBlobRef.current = null;
    setImagePreviewLoading(true);
    (async () => {
      try {
        const response = await fetch(fileUrl, { credentials: 'include' });
        if (!response.ok) throw new Error('preview');
        const blob = await response.blob();
        if (cancelled) return;
        imagePreviewBlobRef.current = { contentId: content.id, blob };
        objectUrl = window.URL.createObjectURL(blob);
        setImagePreviewUrl(objectUrl);
      } catch (e) {
        logError('DocumentModal:imagePreview', e);
        if (!cancelled) setImagePreviewUrl(null);
      } finally {
        if (!cancelled) setImagePreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [isImage, fileUrl, content?.file_path, content?.id]);

  if (!content || !fileUrl) return null;

  // Download handler, platform-aware:
  //  - Desktop (Mac/Windows/Linux): <a download> — triggers real browser save
  //  - Mobile (iOS/Android only, where <a download> is hijacked by the OS):
  //    try Web Share API first, fallback to <a download>
  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const downloadUrl = `${API}/files/${content.file_path}?download=1&filename=${encodeURIComponent(fileName)}`;
      const cached = isImage ? imagePreviewBlobRef.current : null;
      let blob;
      if (cached?.contentId === content.id && cached.blob) {
        blob = cached.blob;
      } else {
        const response = await fetch(downloadUrl, { credentials: 'include' });
        if (!response.ok) throw new Error('Download fallito');
        blob = await response.blob();
      }

      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

      // Mobile: prefer Web Share (opens iOS "Save to Files" / Android share sheet)
      if (isMobile && navigator.canShare) {
        const mimeType = blob.type || 'application/octet-stream';
        const file = new File([blob], fileName, { type: mimeType });
        if (navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({ files: [file], title: fileName });
            markProgressCompleteOnDownload(content.id);
            return;
          } catch (shareErr) {
            if (shareErr && shareErr.name === 'AbortError') return;
            // other errors → fall through to <a download>
          }
        }
      }

      // Desktop + mobile fallback: classic <a download>
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
      markProgressCompleteOnDownload(content.id);
    } catch (err) {
      alert('Impossibile scaricare il file. Riprova.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300]" data-testid="document-modal-backdrop">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={handleBackdropClose} />

      <div
        className="relative w-full h-full md:h-[92vh] md:max-w-6xl md:mx-auto md:my-[4vh] bg-white md:rounded-2xl overflow-hidden shadow-2xl animate-fade-in flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header: rispetta notch / Dynamic Island iOS (safe-area) */}
        <div className="flex items-start justify-between border-b border-zinc-200 flex-shrink-0 gap-2 pt-[calc(0.75rem+env(safe-area-inset-top,0px))] pb-3 pl-[max(0.75rem,env(safe-area-inset-left,0px))] pr-[max(0.75rem,env(safe-area-inset-right,0px))] md:pt-4 md:pb-4 md:px-4">
          <button
            onClick={handleClose}
            className="w-10 h-10 rounded-full hover:bg-zinc-100 flex items-center justify-center text-zinc-700 flex-shrink-0 transition-colors"
            aria-label="Chiudi"
            data-testid="close-document-modal-btn"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="min-w-0 flex-1">
            <h2 className="text-base md:text-lg font-medium text-zinc-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {content.title}
            </h2>
            {content.description && (
              <p
                className="text-zinc-500 text-xs md:text-sm whitespace-pre-wrap break-words max-h-[30vh] overflow-y-auto mt-0.5"
                data-testid="document-modal-description"
              >
                {content.description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-[#D4AF37] hover:bg-[#B8941F] disabled:opacity-60 text-white text-sm font-medium transition-colors"
              data-testid="document-download-btn"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              <span className="hidden sm:inline">{downloading ? 'Scarico...' : 'Scarica'}</span>
            </button>
          </div>
        </div>

        {/* Content area */}
        <div className="flex-1 bg-zinc-100 overflow-hidden relative">
          {isPdf ? (
            <Suspense fallback={
              <div className="w-full h-full flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin" />
              </div>
            }>
              <PdfViewer url={fileUrl} />
            </Suspense>
          ) : isImage ? (
            <div className="w-full h-full flex flex-col min-h-0">
              <div className="flex-1 min-h-0 bg-zinc-900/95">
                {imagePreviewLoading && (
                  <div className="w-full h-full flex items-center justify-center">
                    <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin" />
                  </div>
                )}
                {!imagePreviewLoading && imagePreviewUrl && (
                  <PinchZoom resetKey={imagePreviewUrl}>
                    <div className="flex items-center justify-center p-4 min-h-[50vh]">
                      <img
                        src={imagePreviewUrl}
                        alt={content.title}
                        className="max-w-full w-auto h-auto object-contain rounded-lg shadow-lg select-none"
                        draggable={false}
                      />
                    </div>
                  </PinchZoom>
                )}
                {!imagePreviewLoading && !imagePreviewUrl && (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-zinc-300 text-sm">
                    <ImageIcon className="w-12 h-12 text-[#D4AF37]/80" />
                    <p>Anteprima non disponibile. Usa Scarica.</p>
                  </div>
                )}
              </div>
              <p className="flex-shrink-0 text-center text-xs text-zinc-500 py-2 px-4 bg-zinc-100 border-t border-zinc-200">
                Pizzica con due dita per ingrandire · Scarica per salvare il file
              </p>
            </div>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center p-6 text-center overflow-y-auto">
              <div className="w-20 h-20 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-5">
                <Download className="w-10 h-10 text-[#D4AF37]" />
              </div>
              <h3 className="text-lg font-medium text-zinc-900 mb-2 max-w-md">{content.title}</h3>
              <p className="text-zinc-600 text-sm mb-6 max-w-md">
                Questo formato non ha anteprima. Scarica il file per aprirlo con l'app appropriata.
              </p>
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#D4AF37] hover:bg-[#B8941F] disabled:opacity-60 text-white font-medium transition-colors"
              >
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {downloading ? 'Scarico...' : 'Scarica documento'}
              </button>
              <Button
                onClick={handleClose}
                variant="ghost"
                className="mt-3 text-zinc-500 hover:text-zinc-900"
              >
                Chiudi
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

};

export default DocumentModal;
